import { useState } from "react";
import MainWrapper from "@/components/base/MainWrapper";
import TurnstileWidget from "@/components/base/TurnstileWidget";
import styled from "styled-components";
import { breakpoints } from "@/styles/sizes";
import { CkEditorPagesContentWrapper } from "@/styles/GlobalStyles";
import { fetchPageData } from "@/services/dataFetcher";

const MobileContentWrapper = styled.div`
    @media only screen and (${breakpoints.mobile}) {
        padding: 20px;
    }
`;

const FormWrapper = styled.div`
    margin-top: 40px;
    border-top: 1px solid #ddd;
    padding-top: 30px;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const FormInner = styled.form`
    width: 100%;
    max-width: 440px;
`;

const CheckboxRow = styled.label`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    cursor: pointer;
    margin-bottom: 24px;
    font-size: 15px;
    line-height: 1.5;

    input[type="checkbox"] {
        margin-top: 3px;
        flex-shrink: 0;
        width: 18px;
        height: 18px;
        cursor: pointer;
    }
`;

const Field = styled.div`
    margin-bottom: 16px;

    label {
        display: block;
        font-size: 13px;
        font-weight: 600;
        margin-bottom: 6px;
        text-transform: uppercase;
        letter-spacing: 0.05em;
    }

    input {
        width: 100%;
        padding: 10px 14px;
        border: 1px solid #ccc;
        border-radius: 4px;
        font-size: 15px;
        box-sizing: border-box;

        &:focus {
            outline: none;
            border-color: #333;
        }
    }
`;

const SubmitButton = styled.button`
    background: #222;
    color: #fff;
    border: none;
    padding: 14px 32px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    border-radius: 4px;
    letter-spacing: 0.05em;
    margin-top: 8px;

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    &:hover:not(:disabled) {
        background: #444;
    }
`;

const SuccessMessage = styled.div`
    background: #f0faf0;
    border: 1px solid #b2dfb2;
    color: #2e7d32;
    padding: 16px 20px;
    border-radius: 4px;
    font-size: 15px;
`;

const ErrorMessage = styled.div`
    background: #fff4f4;
    border: 1px solid #f5c6c6;
    color: #c62828;
    padding: 16px 20px;
    border-radius: 4px;
    font-size: 15px;
    margin-top: 12px;
`;

const FieldError = styled.span`
    color: #c62828;
    font-size: 13px;
    margin-top: 4px;
    display: block;
`;

export const getServerSideProps = async (context) => {
    return await fetchPageData("getWaiverUkData", context);
};

const Waiver = ({ data, isMobile }) => {

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [honeypot, setHoneypot] = useState('');
    const [accepted, setAccepted] = useState(false);
    const [status, setStatus] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});
    const [turnstileToken, setTurnstileToken] = useState(null);

    const validateEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

    const handleSubmit = async (e) => {

        e.preventDefault();

        const errors = {};
        if (!firstName.trim()) errors.firstName = 'First name is required.';
        if (!lastName.trim()) errors.lastName = 'Last name is required.';
        if (!email.trim()) errors.email = 'Email is required.';
        else if (!validateEmail(email.trim())) errors.email = 'Please enter a valid email address.';

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            return;
        }
        setFieldErrors({});

        setStatus('loading');
        setErrorMessage(null);

        try {

            const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/waiver/submit`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify({
                    firstName: firstName.trim(),
                    lastName: lastName.trim(),
                    email: email.trim(),
                    waiverType: 'uk',
                    website: honeypot,
                    cf_turnstile_response: turnstileToken
                }),
            });

            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                setErrorMessage(body.message || 'Something went wrong. Please try again.');
                setStatus('error');
                return;
            }
            setStatus('success');

        } catch {

            setErrorMessage('Something went wrong. Please try again.');
            setStatus('error');

        }
    };

    return (
        <MainWrapper
            regionsData={data?.data?.regions}
            headerData={data?.data?.header}
            footerData={data?.data?.footer}
            socialLinks={data?.data?.socialLinks}
            searchOptions={data?.data?.searchOptions}
            isMobileDefault={isMobile}
        >
            <MobileContentWrapper>
                <CkEditorPagesContentWrapper className="container_small">
                    <div dangerouslySetInnerHTML={{ __html: data?.data?.page?.data?.content }} />

                    <FormWrapper>
                        {status === 'success' ? (
                            <SuccessMessage>
                                Thank you. Your waiver has been submitted successfully.
                            </SuccessMessage>
                        ) : (
                            <FormInner onSubmit={handleSubmit}>
                                <input
                                    type="text"
                                    name="website"
                                    value={honeypot}
                                    onChange={(e) => setHoneypot(e.target.value)}
                                    tabIndex="-1"
                                    aria-hidden="true"
                                    autoComplete="off"
                                    style={{ position: 'absolute', left: '-5000px' }}
                                />
                                <CheckboxRow>
                                    <input
                                        type="checkbox"
                                        checked={accepted}
                                        onChange={(e) => setAccepted(e.target.checked)}
                                        required
                                    />
                                    I have read and fully understand the terms of this Tour Waiver and Image Consent and accept them freely.
                                </CheckboxRow>

                                <Field>
                                    <label htmlFor="firstName">First Name</label>
                                    <input
                                        id="firstName"
                                        type="text"
                                        value={firstName}
                                        onChange={(e) => { setFirstName(e.target.value); setFieldErrors(prev => ({ ...prev, firstName: null })); }}
                                    />
                                    {fieldErrors.firstName && <FieldError>{fieldErrors.firstName}</FieldError>}
                                </Field>
                                <Field>
                                    <label htmlFor="lastName">Last Name</label>
                                    <input
                                        id="lastName"
                                        type="text"
                                        value={lastName}
                                        onChange={(e) => { setLastName(e.target.value); setFieldErrors(prev => ({ ...prev, lastName: null })); }}
                                    />
                                    {fieldErrors.lastName && <FieldError>{fieldErrors.lastName}</FieldError>}
                                </Field>
                                <Field>
                                    <label htmlFor="email">Email</label>
                                    <input
                                        id="email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => { setEmail(e.target.value); setFieldErrors(prev => ({ ...prev, email: null })); }}
                                    />
                                    {fieldErrors.email && <FieldError>{fieldErrors.email}</FieldError>}
                                </Field>

                                <TurnstileWidget onToken={setTurnstileToken} style={{ margin: '16px 0' }} />

                                <SubmitButton type="submit" disabled={status === 'loading' || !accepted || !turnstileToken}>
                                    {status === 'loading' ? 'Submitting...' : 'Submit'}
                                </SubmitButton>

                                {status === 'error' && (
                                    <ErrorMessage>{errorMessage}</ErrorMessage>
                                )}
                            </FormInner>
                        )}
                    </FormWrapper>
                </CkEditorPagesContentWrapper>
            </MobileContentWrapper>
        </MainWrapper>
    );
}

export default Waiver;
